// 라이브 라운지 전역 오디오 세션 — 핵심 업그레이드
// 라디오 청취/방송·라이브 콜 연결을 앱 전역에서 1개 유지한다.
// 다른 페이지로 이동해도 오디오가 끊기지 않고, 하단 미니플레이어로 제어한다.
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLiveAdminI18n } from '../i18n/liveAdminTranslations';

const LiveSessionContext = createContext(null);

export const useLiveSession = () => {
  const ctx = useContext(LiveSessionContext);
  if (!ctx) throw new Error('useLiveSession must be used within LiveSessionProvider');
  return ctx;
};

export function LiveSessionProvider({ children }) {
  const { tr } = useLiveAdminI18n();
  // session: { type: 'radio'|'radio-host'|'voice', id, title, path, publish, onEnd }
  const [session, setSession] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [micState, setMicState] = useState('idle'); // idle | connecting | on | muted | error
  const [micError, setMicError] = useState('');
  const [speakingIds, setSpeakingIds] = useState([]);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const roomRef = useRef(null);
  const audioElsRef = useRef([]);
  const sessionRef = useRef(null);

  const withTimeout = (promise, ms, message) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);

  const cleanupAudio = () => {
    audioElsRef.current.forEach((el) => { try { el.remove(); } catch { /* noop */ } });
    audioElsRef.current = [];
  };

  const disconnect = useCallback(async (runOnEnd = false) => {
    const prev = sessionRef.current;
    if (runOnEnd && prev?.onEnd) {
      try { await prev.onEnd(); } catch { /* noop */ }
    }
    if (roomRef.current) {
      try { roomRef.current.disconnect(); } catch { /* noop */ }
      roomRef.current = null;
    }
    cleanupAudio();
    sessionRef.current = null;
    setSession(null);
    setIsMuted(false);
    setMicState('idle');
    setMicError('');
    setSpeakingIds([]);
    setNeedsUnlock(false);
  }, []);

  const enableMicrophone = useCallback(async () => {
    const room = roomRef.current;
    const sess = sessionRef.current;
    if (!room || !sess?.publish) return false;
    setMicState('connecting');
    setMicError('');
    try {
      await room.localParticipant.setMicrophoneEnabled(true);
      setIsMuted(false);
      setMicState('on');
      return true;
    } catch (error) {
      setIsMuted(true);
      setMicState('error');
      setMicError(error?.message || tr('마이크 권한을 확인해주세요.'));
      return false;
    }
  }, []);

  const connect = useCallback(async ({ type, id, title, path, url, token, publish, onEnd }) => {
    await disconnect(true); // 기존 세션은 정리하고 새로 연결 (동시 1세션)
    const { Room, RoomEvent } = await import('livekit-client');
    const room = new Room();

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== 'audio') return;
      const el = track.attach();
      el.dataset.liveAudio = '1';
      document.body.appendChild(el);
      audioElsRef.current.push(el);
      const p = el.play();
      if (p && p.catch) p.catch(() => setNeedsUnlock(true));
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => {
        audioElsRef.current = audioElsRef.current.filter((x) => x !== el);
        el.remove();
      });
    });
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      setSpeakingIds(speakers.map((s) => String(s.identity)));
    });

    await withTimeout(room.connect(url, token), 12000, tr('음성 서버 연결 시간이 초과되었습니다. 다시 시도해주세요.'));
    roomRef.current = room;
    const sess = { type, id, title, path, publish, onEnd };
    sessionRef.current = sess;
    setSession(sess);
    setIsMuted(!publish);
    setMicState(publish ? 'connecting' : 'idle');
    setMicError('');
    if (publish) {
      enableMicrophone();
    }
    return room;
  }, [disconnect, enableMicrophone, tr]);

  // 발언 가능 토큰으로 재연결 (라디오 스피커 초대 수락 등)
  const upgradeToPublisher = useCallback(async (url, token) => {
    const prev = sessionRef.current;
    if (!prev) return null;
    if (roomRef.current) { try { roomRef.current.disconnect(); } catch { /* noop */ } }
    cleanupAudio();
    const { Room, RoomEvent } = await import('livekit-client');
    const room = new Room();
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== 'audio') return;
      const el = track.attach();
      document.body.appendChild(el);
      audioElsRef.current.push(el);
      const p = el.play();
      if (p && p.catch) p.catch(() => setNeedsUnlock(true));
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => el.remove());
    });
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      setSpeakingIds(speakers.map((s) => String(s.identity)));
    });
    await withTimeout(room.connect(url, token), 12000, tr('음성 서버 연결 시간이 초과되었습니다. 다시 시도해주세요.'));
    roomRef.current = room;
    const sess = { ...prev, publish: true };
    sessionRef.current = sess;
    setSession(sess);
    setMicState('connecting');
    setMicError('');
    enableMicrophone();
    return room;
  }, [enableMicrophone, tr]);

  const toggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);
    const sess = sessionRef.current;
    if (sess?.publish && roomRef.current) {
      roomRef.current.localParticipant.setMicrophoneEnabled(!next)
        .then(() => setMicState(next ? 'muted' : 'on'))
        .catch((error) => {
          setMicState('error');
          setMicError(error?.message || tr('마이크 권한을 확인해주세요.'));
        });
    } else {
      audioElsRef.current.forEach((el) => { el.muted = next; });
    }
  }, [isMuted, tr]);

  const unlockAudio = useCallback(() => {
    audioElsRef.current.forEach((el) => { const p = el.play(); if (p && p.catch) p.catch(() => {}); });
    setNeedsUnlock(false);
  }, []);

  const value = {
    session, isMuted, micState, micError, speakingIds, needsUnlock,
    connect, disconnect, toggleMute, unlockAudio, upgradeToPublisher, enableMicrophone,
    getRoom: () => roomRef.current,
  };
  return <LiveSessionContext.Provider value={value}>{children}</LiveSessionContext.Provider>;
}

/* ── 하단 미니플레이어: 세션 페이지 밖에서도 라이브 제어 ── */
export function LiveMiniPlayer() {
  const { session, isMuted, toggleMute, disconnect, needsUnlock, unlockAudio } = useLiveSession();
  const { tr } = useLiveAdminI18n();
  const navigate = useNavigate();
  const location = useLocation();

  if (!session || location.pathname === session.path) return null;

  const typeLabel = session.type === 'voice' ? `🎙 ${tr('라이브 콜')}`
    : session.type === 'radio-host' ? `📻 ${tr('방송 중')}` : `📻 ${tr('라디오')}`;

  return (
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 1200,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderRadius: 14,
      background: 'rgba(23,20,15,0.96)', border: '1px solid rgba(201,169,106,0.4)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)',
      maxWidth: 560, margin: '0 auto',
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: '#e3554d',
        boxShadow: '0 0 8px #e3554d', flexShrink: 0,
        animation: 'pulse 1.6s infinite',
      }} />
      <button
        onClick={() => navigate(session.path)}
        style={{
          flex: 1, background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left', color: '#f4f0e8', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13,
        }}
      >
        <span style={{ color: '#c9a96a', fontWeight: 700, marginRight: 8 }}>{typeLabel}</span>
        {session.title}
      </button>
      {needsUnlock && (
        <button onClick={unlockAudio} style={{
          border: '1px solid #c9a96a', background: 'rgba(201,169,106,0.15)', color: '#e3c789',
          borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer', flexShrink: 0,
        }}>▶ {tr('소리켜기')}</button>
      )}
      <button onClick={toggleMute} aria-label={tr('음소거')} style={{
        border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', flexShrink: 0,
      }}>{isMuted ? '🔇' : '🔊'}</button>
      <button onClick={() => disconnect(true)} aria-label={tr('나가기')} style={{
        border: 'none', background: 'none', color: '#a09884', fontSize: 16, cursor: 'pointer', flexShrink: 0,
      }}>✕</button>
    </div>
  );
}
